'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { LeagueMatchCard } from './LeagueMatchCard'
import { PreseasonHero } from './PreseasonHero'
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion'

interface HeroMatch {
  matchNumber: string
  giornata: number
  date: string
  time: string
  home: string
  away: string
  homeLogoSrc?: string | null
  awayLogoSrc?: string | null
  result: string | null
  sets: string | null
  played: boolean
  venue?: string | null
  referees?: string | null
  competition?: string
  championshipSlug: string
  championshipTitle: string
  poolSlug: string
  stagioneId?: string
  badgePath?: string | null
  /** Link già pronto (tornei): vince sulla URL costruita da girone + numero gara. */
  href?: string | null
}

/**
 * Una slide della hero. La home può mescolare tipi diversi — la partita in
 * evidenza e il countdown di pre-season — dentro un unico slider: prima erano
 * due blocchi impilati, ora ruotano insieme quando c'è più di una slide.
 */
export type HeroSlide =
  | { kind: 'match'; match: HeroMatch }
  | { kind: 'countdown'; firstMatchDate: string }

interface HeroCarouselProps {
  slides: HeroSlide[]
}

const SLIDE_MS = 5000
const SLIDE_COMMIT_MS = 520
const ANIM = 'opacity 0.35s cubic-bezier(0.22, 1, 0.36, 1), transform 0.5s cubic-bezier(0.22, 1, 0.36, 1), filter 0.5s cubic-bezier(0.22, 1, 0.36, 1)'
const HERO_ANGLES = ['205deg', '145deg', '255deg', '325deg'] as const

function matchUrl(m: HeroMatch) {
  if (m.href) return m.href
  const base = `/campionati/${m.championshipSlug}/${m.poolSlug}/${m.matchNumber}`
  return m.stagioneId ? `${base}?stagione=${m.stagioneId}` : base
}

function heroAngle(idx: number) {
  return HERO_ANGLES[idx % HERO_ANGLES.length]
}

function slideLabel(slide: HeroSlide, idx: number) {
  return slide.kind === 'countdown' ? 'Countdown pre-season' : `Match ${idx + 1}`
}

export function HeroCarousel({ slides }: HeroCarouselProps) {
  const reduceMotion = usePrefersReducedMotion()
  const [activeIdx, setActiveIdx] = useState(0)
  const [dir, setDir] = useState<'next' | 'prev'>('next')
  const [sliding, setSliding] = useState(false)
  const pendingIdx = useRef<number | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const total = slides.length

  function startAutoplay() {
    if (intervalRef.current) clearInterval(intervalRef.current)
    if (reduceMotion || total <= 1) return
    intervalRef.current = setInterval(() => {
      pendingIdx.current = null // use default next
      setDir('next')
      setSliding(true)
    }, SLIDE_MS)
  }

  useEffect(() => {
    startAutoplay()
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total, reduceMotion])

  // Commit slide: update activeIdx after animation
  useEffect(() => {
    if (!sliding) return
    const t = setTimeout(() => {
      setActiveIdx((prev) => {
        if (pendingIdx.current !== null) return pendingIdx.current
        return dir === 'next' ? (prev + 1) % total : (prev - 1 + total) % total
      })
      pendingIdx.current = null
      setSliding(false)
    }, SLIDE_COMMIT_MS)
    return () => clearTimeout(t)
  }, [sliding, dir, total])

  function goTo(idx: number) {
    if (idx === activeIdx || sliding) return
    pendingIdx.current = idx
    setDir(idx > activeIdx ? 'next' : 'prev')
    setSliding(true)
    startAutoplay()
  }

  if (total === 0) return null

  const nextIdx = pendingIdx.current !== null
    ? pendingIdx.current
    : dir === 'next' ? (activeIdx + 1) % total : (activeIdx - 1 + total) % total

  return (
    <div>
      {/*
       * Tutte le slide vivono nella stessa cella di una griglia (`grid-area: 1/1`):
       * così l'altezza della hero è quella della slide più alta e il passaggio da
       * una all'altra non fa saltare il contenitore né taglia la slide in arrivo.
       * Le slide nascoste restano montate ma fuori dal flusso di focus e clic.
       */}
      {/* La traccia della cella unica deve poter scendere sotto il **min-content**
          della slide più larga: con la `1fr` implicita (= `minmax(auto, 1fr)`) la
          traccia veniva dimensionata sul min-content della card hero (la griglia
          `1fr auto 1fr` delle squadre), la slide cresceva oltre lo `.shell` e
          veniva tagliata dal bordo destro dello schermo (su 390 px la card
          misurava 519 px) — «OSPITI» si leggeva «OSPIT» e il nome ospite
          spariva oltre il bordo. `minmax(0, 1fr)` chiude la traccia sulla
          larghezza disponibile e lascia alle card il compito di degradare. */}
      <div className="relative grid grid-cols-[minmax(0,1fr)] overflow-hidden rounded-[24px] md:rounded-[32px]">
        <div className="pointer-events-none absolute inset-0 z-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.18),_transparent_55%)]" />

        {slides.map((slide, i) => {
          const isActive = i === activeIdx
          const isNext = sliding && i === nextIdx
          const visible = isActive || isNext
          return (
            <div
              key={`${slide.kind}-${i}`}
              aria-hidden={visible ? undefined : true}
              inert={visible ? undefined : true}
              className="relative z-10 min-w-0"
              style={{
                gridArea: '1 / 1',
                ['--hero-angle' as string]: heroAngle(i),
                opacity: isNext ? 1 : isActive ? (sliding ? 0 : 1) : 0,
                filter: isNext ? 'blur(0px)' : isActive ? (sliding ? 'blur(10px)' : 'blur(0px)') : 'blur(14px)',
                transform: isNext ? 'scale(1)' : isActive ? (sliding ? 'scale(1.035)' : 'scale(1)') : 'scale(0.965)',
                transition: reduceMotion ? 'none' : ANIM,
                transformOrigin: 'center center',
                pointerEvents: visible ? undefined : 'none',
              }}
            >
              {slide.kind === 'match' ? (
                <Link href={matchUrl(slide.match)} className="block h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--league-brand-vivid)] rounded-[24px] md:rounded-[32px]">
                  <LeagueMatchCard match={slide.match} variant="hero" />
                </Link>
              ) : (
                <PreseasonHero firstMatchDate={slide.firstMatchDate} />
              )}
            </div>
          )
        })}
      </div>

      {/* Dots */}
      {total > 1 && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {slides.map((slide, i) => (
            <button
              key={`dot-${slide.kind}-${i}`}
              onClick={() => goTo(i)}
              aria-label={slideLabel(slide, i)}
              aria-current={i === activeIdx ? 'true' : undefined}
              className="league-press flex min-h-11 min-w-11 items-center justify-center rounded-full transition-colors hover:bg-[color:var(--league-surface-alt)]"
            >
              <span
                aria-hidden
                className={`rounded-full ${i === activeIdx
                  ? 'h-2 w-6 bg-[color:var(--league-brand-vivid)]'
                  : 'h-2 w-2 bg-[color:var(--league-line-strong)]'
                }`}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
